/**
 * Confirmation de réservation v2 — route class/:id/confirmation (SPEC § 4.3 : CO1 à CO3, R6).
 *
 * Une route plutôt qu'un état de la fiche : l'actualisation garde la confirmation. Tout est relu :
 * le cours et ses réservations (`GET class/{id}`), les visites, et les crédits restants
 * (`GET client/services`, affichés seulement une fois relus après la réservation, jamais
 * calculés). Billet : studio, cours, nom de la marque en texte, quand, coach, spot(s), où.
 *
 * Affichée dès la réponse du serveur (retour du 07/10) : cours en cache complété des spots de l'état de navigation
 * tant que la relecture n'est pas arrivée (lib/booking confirmationSpots : spot de l'invité ajouté à un cours déjà
 * réservé, revue du 08/10) ; crédits « Mise à jour de vos crédits… » jusqu'à la relecture partie à la réponse du
 * serveur (useBooking.refreshAfterBooking), à laquelle la page se raccroche sans la relancer.
 * Boutons : « Ajouter à mon agenda », « Réserver un autre cours » ; pas de « Mon compte » (R6). Puis « Votre prochaine
 * séance » (CO4, CO5, demande de Luc du 08/10 ; CO6, demande de Daphné du 08/10) : le même cours la semaine prochaine
 * s'il est ouvert, un autre cours avec le coach, un autre cours au studio (components/FollowUps, useFollowUps).
 */
import { ScaleFade, Skeleton } from '@chakra-ui/react';
import { useFetchClass, useListClientServices, useListVisits } from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { cssThemeTokenReader, readSettings, resolveTokens } from '../../theme/tokens';
import type { ConfirmationState } from './ClassScreen';
import { FollowUps } from './components/FollowUps';
import { Icon } from './components/Icon';
import { useClassToast } from './components/toast';
import { confirmationSpots, creditsReadSince, creditsRequiredFor, isCommunityClassName, isRunClubClass, recentBookedAt } from './lib/booking';
import { classColors, classColorVars } from './lib/colors';
import { addressLine, coachName, plural, rendezVousLabel, studioLabel, ticketDate } from './lib/format';
import { downloadIcs } from './lib/ics';
import { locationNameOf, readEventSplit } from '../../lib/planning/eventStudios';
import { useFollowUps } from './useFollowUps';
import { injectV2Css } from '../../styles/inject';
import classCss from './class.css?inline';
import confirmationCss from './confirmation.css?inline';

injectV2Css('screens/Class/class.css', classCss);
injectV2Css('screens/Class/confirmation.css', confirmationCss);

export function ClassConfirmationScreenV2() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useClassToast();
  const state = (location.state as ConfirmationState | null) ?? null;
  const isEnrollment = state?.isEnrollment ?? false;
  const { isLoggedIn, user } = useUserContext();

  const colorVars = useMemo(() => {
    try {
      return classColorVars(classColors(resolveTokens(readSettings(), cssThemeTokenReader())));
    } catch {
      return {};
    }
  }, []);

  const classQuery = useFetchClass(id, isEnrollment);
  const servicesQuery = useListClientServices();
  const visitsQuery = useListVisits();
  const visits = visitsQuery?.data;

  const cls = classQuery.data;
  const brandName = (typeof mondayMindbody !== 'undefined' && mondayMindbody?.customization?.brandName) || '';
  const siteId = String((typeof mondayMindbody !== 'undefined' && mondayMindbody?.siteId) || '');

  const [bookedAt] = useState(() => recentBookedAt(state?.bookedAt, Date.now()));

  const spots = useMemo(() => {
    const reservations = (cls?.PickASpot?.reservations ?? []).filter((r) => user?.Id && String(r.memberExternalId) === String(user.Id));
    const fromClass = reservations
      .sort((a, b) => Number(/guest/i.test(a.reservationType ?? '')) - Number(/guest/i.test(b.reservationType ?? '')))
      .map((r) => r.spot?.spotNumber)
      .filter((n): n is number => !!n);
    return confirmationSpots(fromClass, state?.spots, bookedAt);
  }, [cls, user?.Id, state?.spots, bookedAt]);

  const bookedVisits = (visits ?? []).filter(
    (v) => v.ClassId === Number(id) && v.AppointmentStatus === 'Booked' && v.ClientId === user?.Id,
  );
  const places = Math.max(spots.length, bookedVisits.length, 1);
  const creditsRequired = creditsRequiredFor(cls);
  const freeOfCharge = isCommunityClassName(cls?.ClassDescription?.Name) || isRunClubClass(cls, siteId);

  // CO1 : crédits RELUS après la réservation, jamais calculés. Arrivée juste après la réservation (bookedAt de moins
  // de 30 s) : la relecture partie à la réponse du serveur est attendue (cancelRefetch: false, elle n'est pas
  // relancée), ou déjà arrivée (données plus récentes que la réservation). Sinon (actualisation, retour plus tard) :
  // relecture au montage, malgré le staleTime global de 60 s du QueryClient.
  const queryClient = useQueryClient();
  const [creditsFresh, setCreditsFresh] = useState(false);
  const refetchedOnce = useRef(false);
  const { refetch: refetchServices } = servicesQuery;
  useEffect(() => {
    if (!isLoggedIn || refetchedOnce.current) return;
    refetchedOnce.current = true;
    if (bookedAt !== null && creditsReadSince(queryClient.getQueryState(['/client/services', user?.Id]), bookedAt)) {
      setCreditsFresh(true);
      return;
    }
    refetchServices({ cancelRefetch: bookedAt === null }).finally(() => setCreditsFresh(true));
  }, [isLoggedIn, refetchServices, bookedAt, queryClient, user?.Id]);
  const services = servicesQuery.data ?? [];
  const debited = services.find((s) => Number(s.Id) === state?.serviceId);
  const remainingTotal = services.reduce((sum, s) => sum + (Number.isFinite(s.Remaining) ? s.Remaining : 0), 0);
  // Suites (CO4, CO5, CO6) : connues avec le cours, les semaines du planning et les cours du client (même en cache).
  const followUps = useFollowUps(cls, {
    visits,
    visitsReady: visits !== undefined || !!visitsQuery?.isError,
    isEnrollment,
  });
  const remainingLabel =
    debited && services.length > 1
      ? `Il\u00a0vous\u00a0reste ${plural(debited.Remaining ?? 0, 'crédit')} sur le pack «\u00a0${debited.Name}\u00a0».`
      : `Il\u00a0vous\u00a0reste ${plural(remainingTotal, 'crédit')}.`;

  useEffect(() => {
    if (state && creditsFresh && !freeOfCharge) toast(`C’est réservé. ${remainingLabel}`, `mmv2-booked-${id}`);
  }, [state, creditsFresh, freeOfCharge, remainingLabel, toast, id]);

  if (!isLoggedIn) return <Navigate to={`/class/${id}`} replace />;

  if (classQuery.isLoading) {
    return (
      <div className="mmv2-class-page mmv2-confirm" style={colorVars as CSSProperties} aria-busy="true">
        <Skeleton w="74px" h="74px" borderRadius="full" mx="auto" />
        <Skeleton h="40px" w="min(420px, 80%)" mx="auto" mt="28px" />
        <Skeleton h="320px" mt="28px" />
      </div>
    );
  }

  const isBooked = spots.length > 0 || bookedVisits.length > 0;
  if (!cls || (!isBooked && !state)) return <Navigate to={`/class/${id}${isEnrollment ? '/enrollment' : ''}`} replace />;

  const start = new Date(cls.StartDateTime);
  // Studio virtuel du studio Monday Event de Paris (« Run Club », Special Events), comme au planning.
  const studio = studioLabel(locationNameOf(cls, readEventSplit()), brandName);
  const lede =
    spots.length >= 2 ? 'Deux spots réservés — le vôtre et celui de votre invité.' : spots.length === 1 ? 'Votre spot est réservé.' : 'Votre place est réservée.';

  const addToCalendar = () =>
    downloadIcs({
      uid: `class-${cls.Id}-${siteId}@monday-mindbody`,
      start: cls.StartDateTime,
      end: cls.EndDateTime,
      summary: `${cls.ClassDescription?.Name ?? 'Cours'} — ${studio}`,
      location: addressLine(cls.Location),
      description: [coachName(cls.Staff) ? `Coach\u00a0: ${coachName(cls.Staff)}` : '', spots.length ? `Spot${spots.length > 1 ? 's' : ''}\u00a0: ${spots.map((n) => `#${n}`).join(', ')}` : '']
        .filter(Boolean)
        .join('. '),
      url: `${window.location.origin}${window.location.pathname}#/class/${cls.Id}`,
    });

  return (
    <div
      className="mmv2-class-page mmv2-confirm"
      style={colorVars as CSSProperties}
      data-followups={followUps.ready ? 'ready' : 'pending'}
    >
      <ScaleFade in initialScale={0.6}>
        <div className="mmv2-confirm__tick" aria-hidden="true">
          <Icon name="check" size={34} stroke={2.6} />
        </div>
      </ScaleFade>
      <span className="mmv2-kicker mmv2-confirm__kicker">C’est confirmé</span>
      <h1 className="mmv2-h1 mmv2-confirm__title">
        {/* Trait d'union dégagé : l'interlettrage négatif de la police condensée le collait au Z et au V (05/10). */}
        {rendezVousLabel(start)
          .split('-')
          .flatMap((part, i) => (i === 0 ? [part] : [<span key={i} className="mmv2-confirm__hyphen">-</span>, part]))}
      </h1>
      <p className="mmv2-lede mmv2-confirm__lede" aria-live="polite">
        {lede}{' '}
        {freeOfCharge ? null : creditsFresh ? remainingLabel : <span className="mmv2-confirm__pending">Mise à jour de vos crédits…</span>}
        {creditsRequired > 1 && !freeOfCharge ? (
          <span className="mmv2-confirm__multi">
            {' '}
            {creditsRequired * places}&nbsp;crédits ont été décomptés de votre forfait.
          </span>
        ) : null}
      </p>

      <article className="mmv2-ticket" aria-label="Votre billet">
        <div className="mmv2-ticket__head">
          <div>
            <span className="mmv2-kicker mmv2-kicker--brand">{studio}</span>
            <h2 className="mmv2-h3 mmv2-ticket__title">{cls.ClassDescription?.Name}</h2>
          </div>
          {brandName ? <span className="mmv2-ticket__brand">{brandName}</span> : null}
        </div>
        <div className="mmv2-ticket__cut" aria-hidden="true" />
        <dl className="mmv2-ticket__rows">
          <div className="mmv2-kv">
            <dt>Quand</dt>
            <dd>{ticketDate(start)}</dd>
          </div>
          {coachName(cls.Staff) ? (
            <div className="mmv2-kv">
              <dt>Coach</dt>
              <dd>{coachName(cls.Staff)}</dd>
            </div>
          ) : null}
          {spots[0] ? (
            <div className="mmv2-kv">
              <dt>Votre spot</dt>
              <dd>#{spots[0]}</dd>
            </div>
          ) : null}
          {spots.slice(1).map((n) => (
            <div className="mmv2-kv" key={n}>
              <dt>Spot de votre invité</dt>
              <dd>#{n}</dd>
            </div>
          ))}
          {addressLine(cls.Location) ? (
            <div className="mmv2-kv">
              <dt>Où</dt>
              <dd>{addressLine(cls.Location)}</dd>
            </div>
          ) : null}
        </dl>
      </article>

      <div className="mmv2-confirm__actions">
        <button type="button" className="mmv2-btn mmv2-btn--dark" onClick={addToCalendar}>
          <Icon name="cal" />
          Ajouter à mon agenda
        </button>
        <button type="button" className="mmv2-btn mmv2-btn--ghost" onClick={() => navigate('/')}>
          Réserver un autre cours
        </button>
      </div>

      <FollowUps
        ready={followUps.ready}
        nextWeek={followUps.nextWeek}
        coach={followUps.coach}
        studio={followUps.studio}
        studioSlot={followUps.studioSlot}
      />
    </div>
  );
}
